import { isInteger, isArray } from 'lodash';
import React, { useCallback, memo } from 'react';
import styled from 'styled-components';

import Avatar from '../../Clients/Avatar';
import Button from '../../../Button';
import Count from '../../Common/Count';
import Name from '../../Clients/Name';
import TagList from '../../Tags/TagList';
import TicketId from '../../Common/CopyOnClick';
import TicketStatus from '../TicketStatus';
import TicketType from '../TicketType';
import { TICKET_STATUS_PLACEMENT } from '../TicketStatus/constants';

import { ArrowUpRight, Copy } from '@icons/common';
import { CompactManagerType, TicketType as TicketTypeType } from 'types/types';
import CopyOnClick from '../../Common/CopyOnClick';

interface ITicketCardWithManager {
  className?: string;
  manager?: CompactManagerType | null;
  ticket: TicketTypeType;
  getOrderURLById: (orderId: string) => string;
  onSelect: (ticketId: string) => void;
}

const TicketCardWithManager = ({
  manager = null,
  ticket,
  onSelect = ticketId => null,
  getOrderURLById,
  ...props
}: ITicketCardWithManager) => {
  if (!ticket) {
    return null;
  }

  const {
    id,
    subject,
    subtype,
    subAgentName,
    status,
    tags,
    type,
    unreadAmount,
    orderIds,
  } = ticket;
  const { priority, time, title } = status;

  const isValidCount =
    !!unreadAmount && isInteger(unreadAmount) && unreadAmount > 0;
  const onClickHandler = useCallback(() => onSelect(ticket.id), [ticket]);

  let managerComponent: React.ReactElement | null = null;
  if (manager) {
    const { avatarUrl, fullName, number } = manager;
    managerComponent = (
      <StyledManager>
        <StyledAvatar name={fullName} url={avatarUrl} />
        <StyledName name={fullName} />
        {number && <StyledNumber>{number}</StyledNumber>}
      </StyledManager>
    );
  }

  return (
    <StyledTicketCardWithManager functional onClick={onClickHandler} {...props}>
      <StyledTicketId>
        Тикет {id}
        <CopyOnClick
          valueForCopy={`${window.location.hostname}/tickets/${id}`}
          hintText="Скопировать ссылку на тикет"
        >
          <Copy width={16} />
        </CopyOnClick>
      </StyledTicketId>
      <StyledWrapper>
        <StyledTicketType subtype={subtype} type={type} />
        <StyledCountContainer>
          {isValidCount && <Count value={unreadAmount} />}
        </StyledCountContainer>
      </StyledWrapper>
      <StyledSubject title={subject}>{subject}</StyledSubject>
      <StyledStatus
        priority={priority}
        time={time}
        title={title}
        placement={TICKET_STATUS_PLACEMENT.TICKET_CARD_WITH_MANAGER}
      />
      {managerComponent}
      {!!subAgentName && (
        <StyledSubAgentName>{subAgentName}</StyledSubAgentName>
      )}
      <StyledTags tags={tags} title={(isArray(tags) ? tags : []).join(', ')} />
      {isArray(orderIds) &&
        orderIds.map(orderId => (
          <StyledOrderLink
            onClick={event => {
              event.stopPropagation();
            }}
            href={getOrderURLById(orderId)}
            target="_blank"
            rel="noopener noreferrer"
            key={orderId}
          >
            <StyledOrderArrow />
            Заказ: {orderId}
          </StyledOrderLink>
        ))}
    </StyledTicketCardWithManager>
  );
};

const StyledTicketId = styled.div`
  display: flex;
  gap: 4px;
`;

const StyledTicketCardWithManager = styled(Button)`
  background-color: #f5f3f2;
  border-radius: 2px;
  display: flex;
  flex-direction: column;
  height: 136px;
  padding: 6px 10px;
  width: 100%;
`;

const StyledWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  * + & {
    margin-top: 7px;
  }
`;

const StyledTicketType = styled(TicketType)`
  margin-right: 12px;
`;

const StyledCountContainer = styled.div`
  width: 23px;
`;

const StyledSubject = styled.div`
  color: #17181a;
  font-size: 14px;
  font-weight: 700;
  line-height: 17px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  text-align: left;

  * + & {
    margin-top: 3px;
  }
`;

const StyledStatus = styled(TicketStatus)`
  font-size: 14px;
  text-align: left;
`;

const StyledOrderLink = styled.a`
  font-size: 12px;
  line-height: 16px;
  color: #007aff;
  z-index: 10;
`;

const StyledOrderArrow = styled(ArrowUpRight)`
  width: 9px;
  height: 8px;
  display: inline-block;
  margin-right: 4px;
`;

const StyledManager = styled.div`
  color: #4d4d4d;
  display: flex;
  font-size: 14px;

  * + & {
    margin-top: 8px;
  }
`;

const StyledAvatar = styled(Avatar)`
  height: 18px;
  width: 18px;
`;

const StyledName = styled(Name)`
  font-weight: normal;

  * + & {
    margin-left: 7px;
  }
`;

const StyledNumber = styled.div`
  :before {
    content: '\u22C5';
    margin: 0 5px;
  }
`;

const StyledTags = styled(TagList)`
  color: #919499;
  display: block;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  text-align: left;
`;

const StyledSubAgentName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #4b137b;
  margin: 5px 0;
`;

export default memo(TicketCardWithManager);
